import { Me } from '../types';

interface ROUTE_TYPE {
  path: string;
  type?: string;
  roles: string[];
  permissions?: string[];
}

export const getPermissionLegalName = (RESOURCE_NAME: string) => {
  switch (RESOURCE_NAME) {
    case 'INTERNAL_CHALLENGE_MANAGEMENT':
      return 'Internal challenge manager';
    case 'OPEN_CHALLENGE_MANAGEMENT':
      return 'Open challenge manager';
    case 'A3_EXPERT':
      return 'A3 expert';
    case 'A3_MANAGER':
      return 'A3 manager';
    case 'MD_ACCESS':
      return 'Managing Director (MD) Dashboard';
    case 'COMMUNITY_MANAGER':
      return 'Community manager';
    case 'EVENT':
      return 'Event permission';
    default:
      return RESOURCE_NAME.toLocaleLowerCase()
        .replace(/[-_]/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());
  }
};

export const PERMISSIONS = {
  ADMIN: 'ADMIN',
  INTERNAL_CHALLENGE_MANAGEMENT: 'INTERNAL_CHALLENGE_MANAGEMENT',
  OPEN_CHALLENGE_MANAGEMENT: 'OPEN_CHALLENGE_MANAGEMENT',
  A3_EXPERT: 'A3_EXPERT',
  A3_MANAGER: 'A3_MANAGER',
  MD_ACCESS: 'MD_ACCESS',
  COMMUNITY_MANAGER: 'COMMUNITY_MANAGER',
  EVENT: 'EVENT',
  CHANNEL_REVIEWER: 'CHANNEL_REVIEWER',
  CHANNEL_EXPERT: 'CHANNEL_EXPERT',
};

const DASHBOARD = [
  PERMISSIONS.ADMIN,
  PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
  PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT,
];
const ADMIN_OR_INTRA = [
  PERMISSIONS.ADMIN,
  PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
];
const ADMIN_OR_OPEN = [
  PERMISSIONS.ADMIN,
  PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT,
];

export const AUTH_ROUTES = [
  { path: '/login' },
  { path: '/join' },
  { path: '/forgot-password' },
  { path: '/reset-password' },
  { path: '/confirm' },
  { path: '/migration' },
  { path: '/migration/confirm' },
];

export const PLATFORM_OPEN_ROUTES = [
  { path: '/' },
  { path: '/bridge' },
  { path: '/community' },
  { path: '/community/[id]' },
  { path: '/entrepreneurship' },
  { path: '/entrepreneurship/challenge/[id]' },
  { path: '/entrepreneurship/community/[id]' },
  { path: '/portfolio' },
  { path: '/pitch-arena' },
  { path: '/pitch-arena/[id]' },
  { path: '/company/[companyId]' },
  { path: '/discover' },
  { path: '/403' },
  { path: '/404' },
  { path: '/kanban' },
];

export const PLATFORM_PROTECTED_ROUTES: ROUTE_TYPE[] = [
  {
    path: '/intrapreneurship/challenge/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/basics',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/basics',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/document',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/questionnaire',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/success',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/team',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/challenge/[id]/application/[applicationId]/video',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/intrapreneurship/idea/solution/create',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/basics',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/basics',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/business-scope',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/geographical-scope',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/key-aspects',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/key-contacts',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/KPIs-and-other-targets',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/status',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/success',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/timeline',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/proof-of-concepts/create/[id]/vendors',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/my-profile/space/challenge-ideas',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/my-profile/space/intra-challenges',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/my-profile/space/open-challenges',
    roles: ['ADMIN', 'EXTERNAL_USER'],
  },
  {
    path: '/my-profile/space/pitch',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
  },
  {
    path: '/my-profile/space/poc',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/my-profile/space/posts',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
  },
  {
    path: '/my-profile/space/solution-ideas',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/basics',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/[applicationId]',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/[applicationId]/basics',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/[applicationId]/document',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/[applicationId]/questionnaire',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/entrepreneurship/challenge/[id]/application/[applicationId]/success',
    type: 'external',
    roles: ['EXTERNAL_USER'],
  },
  {
    path: '/idea',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/idea/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/a3',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/a3/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/a3/create',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/a3/create/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/innovation',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/innovation-list',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/innovation/[id]',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/innovation/invitation',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/leaderboard',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/idea-hub',
    type: 'internal',
    roles: ['ADMIN', 'INTRAPRENEUR'],
  },
  {
    path: '/a3/md-dashboard',
    type: 'internal',
    roles: ['ADMIN'],
    permissions: [PERMISSIONS.MD_ACCESS, PERMISSIONS.A3_MANAGER],
  },
];

export const ADMIN_PROTECTED_ROUTES: ROUTE_TYPE[] = [
  {
    path: '/',
    roles: ['ADMIN'],
    permissions: DASHBOARD,
  },
  {
    path: '/audit-log',
    roles: ['ADMIN'],
  },
  {
    path: '/company-management',
    roles: ['ADMIN'],
  },
  {
    path: '/company-management/[companyId]',
    roles: ['ADMIN'],
  },
  {
    path: '/company-management/showcase',
    roles: ['ADMIN'],
  },
  {
    path: '/content-management',
    roles: ['ADMIN'],
  },
  {
    path: '/content-management/post/[id]',
    roles: ['ADMIN'],
  },
  {
    path: '/entrepreneurship/challenge',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [...ADMIN_OR_OPEN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/entrepreneurship/challenge/[id]/basic-info',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/discussion-wall',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/management',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/media',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/preview',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/publish',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/published',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/questionnaire',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/requirements',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/evaluation',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/collaborator',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/timeline',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/[id]/kanban',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/entrepreneurship/challenge/basic-info',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_OPEN,
  },
  {
    path: '/idea-management',
    roles: ['ADMIN'],
  },
  {
    path: '/idea-management/[id]',
    roles: ['ADMIN'],
  },
  {
    path: '/idea-hub',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: [...ADMIN_OR_INTRA, PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT],
  },
  {
    path: '/idea-hub/dashboard',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: [...ADMIN_OR_INTRA, PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT],
  },
  {
    path: '/idea-hub/evaluate',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: [...ADMIN_OR_INTRA, PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT],
  },
  {
    path: '/intrapreneurship/challenge',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge-ideas',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge-ideas/[id]',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge-ideas/submission',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/basic-info',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/discussion-wall',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/management',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/kanban',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/media',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/collaborator',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/idea-channel',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/preview',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/publish',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/published',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/questionnaire',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/[id]/timeline',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/challenge/basic-info',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: ADMIN_OR_INTRA,
  },
  {
    path: '/intrapreneurship/solution-ideas',
    roles: ['ADMIN'],
  },
  {
    path: '/intrapreneurship/solution-ideas/[id]',
    roles: ['ADMIN'],
  },
  {
    path: '/intrapreneurship/solution-ideas/submission',
    roles: ['ADMIN'],
  },
  {
    path: '/notification',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: [...ADMIN_OR_INTRA, ...ADMIN_OR_OPEN],
  },
  {
    path: '/pitch-management',
    roles: ['ADMIN'],
  },
  {
    path: '/pitch-management/pitch/[id]',
    roles: ['ADMIN'],
  },
  {
    path: '/proof-of-concept',
    roles: ['ADMIN'],
  },
  {
    path: '/proof-of-concept/[id]',
    roles: ['ADMIN'],
  },
  {
    path: '/tag-management',
    roles: ['ADMIN'],
  },
  {
    path: '/topic-management',
    roles: ['ADMIN'],
  },
  {
    path: '/user-management',
    roles: ['ADMIN', 'INTRAPRENEUR'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.A3_MANAGER],
  },
  {
    path: '/survey',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/survey/create/basics',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/survey/create/[id]',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/survey/create/[id]/basics',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/survey/create/[id]/questions',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/survey/create/[id]/success',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/community',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/community/[id]',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
  {
    path: '/announcement',
    roles: ['ADMIN', 'INTRAPRENEUR', 'EXTERNAL_USER'],
    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.COMMUNITY_MANAGER],
  },
];

export const checkPermission = (me?: Me, permissions?: string[]) => {
  if (!permissions || !me?.permissions) {
    return false;
  }
  for (let i = 0; i < permissions.length; i++) {
    for (let j = 0; j < me.permissions.length; j++) {
      if (
        me.permissions[j].toUpperCase().includes(permissions[i].toUpperCase())
      ) {
        return true;
      }
    }
  }
  return false;
};
